import {
  CascaderField,
  ContentItem,
  ContentItems,
  ContentWrapper,
  DatePickerField,
  type OperationType,
  TextAreaInputField,
  TextInputField,
} from '@/components';
import { SelectMinistryField } from '@/features/master-table/components';
import type { RsFormSchema } from '@/features/project/projects/_models/project';
import { UserSelectField } from '@/features/tenant-manager/components';
import { useOrganizationOptionsRecursive } from '@/features/tenant-manager/hooks';
import type { FC } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';
import { JumlahTargetPenerimaManfaatArrayField } from './JumlahTargetPenerimaManfaatArrayField';
import { RelatedProjectArrayField } from './RelatedProjectArrayField';

type FormValues = z.infer<typeof RsFormSchema>;
type InitialFormValues = {
  data: Partial<FormValues['data']>;
};

type Props = {
  operation: OperationType;
  ignoredIdsForRelatedProject?: string[];
};

export const RsProjectFields: FC<Props> = ({
  operation,
  ignoredIdsForRelatedProject,
}) => {
  const { t } = useTranslation();
  const {
    watch,
    control,
    setValue: _setValue,
    formState: { errors },
    resetField,
  } = useFormContext<InitialFormValues, unknown, FormValues>();
  const [fiscalYear, _implementationMethods] = watch([
    'data.fiscalYear',
    'data.implementationMethods',
  ]);

  const organizationOptions = useOrganizationOptionsRecursive({
    params: { referenceFiscalYear: fiscalYear?.year().toString() },
    enabled: !!fiscalYear,
  });

  return (
    <>
      <ContentWrapper title={t('project-Identitas-Program')}>
        <ContentItem borderBottom="0" paddingBottom={0}>
          <ContentItems
            items={[
              <DatePickerField
                label={t('created-fiscal-year')}
                key="data.fiscalYear"
                name="data.fiscalYear"
                control={control}
                errors={errors}
                picker="year"
                disabled={operation === 'edit'}
                required
                fieldStyle={{ width: 200 }}
                shouldCoexistOnChange
                onChange={() => {
                  resetField('data.mainCostIds', { defaultValue: [] });
                }}
              />,
            ]}
          />
          <ContentItems
            items={[
              <TextInputField
                key="data.name"
                name="data.name"
                autocompleteKey="rsProject.name"
                label={t('project-name')}
                control={control}
                errors={errors}
                required
              />,
            ]}
          />

          <ContentItems
            items={[
              <TextAreaInputField
                key="data.indonesiaData.acuanProgram"
                name="data.indonesiaData.acuanProgram"
                label={t('project-reference')}
                control={control}
                errors={errors}
                rows={3}
              />,
              <TextAreaInputField
                key="data.indonesiaData.dasarHukumAtauKebijakan"
                name="data.indonesiaData.dasarHukumAtauKebijakan"
                label={t('legal-basis')}
                control={control}
                errors={errors}
                rows={3}
              />,
            ]}
          />
          <ContentItems
            items={[
              <UserSelectField
                key="data.peopleInChargeIds"
                name="data.peopleInChargeIds"
                label={t('people-in-charge')}
                control={control}
                errors={errors}
                mode="multiple"
                organizationFilter="modal"
                useNotInOption
                required
              />,
              <UserSelectField
                useNotInOption
                key="data.businessManagerIds"
                name="data.businessManagerIds"
                mode="multiple"
                label={t('business-manager')}
                control={control}
                errors={errors}
                organizationFilter="modal"
                required
              />,
            ]}
          />
          <ContentItems
            items={[
              <CascaderField
                key="data.organizationIds"
                name="data.organizationIds"
                label={t('pelaksana-program')}
                control={control}
                errors={errors}
                options={organizationOptions.data}
                enableIntermediate
                disabled={!fiscalYear}
                required
                loading={organizationOptions.isLoading}
                useNotInOption
              />,
              <SelectMinistryField
                key="data.ministryId"
                control={control}
                errors={errors}
                name="data.ministryId"
              />,
            ]}
          />
        </ContentItem>
      </ContentWrapper>

      <ContentWrapper title={t('project-Alasan-dan-Tujuan-Review')}>
        <ContentItem borderBottom="0" paddingBottom={0}>
          <ContentItems
            items={[
              <TextAreaInputField
                key="alasanReview"
                name="data.indonesiaData.alasanReview"
                label={t('project-alasan-review')}
                control={control}
                errors={errors}
                rows={3}
              />,
              <TextAreaInputField
                key="tujuanReview"
                name="data.indonesiaData.tujuanReview"
                label={t('project-tujuan-review')}
                control={control}
                errors={errors}
                rows={3}
              />,
            ]}
          />
        </ContentItem>
      </ContentWrapper>

      <ContentWrapper title={t('project-Ringkasan-Program')}>
        <ContentItem borderBottom="0" paddingBottom={0}>
          <ContentItems
            items={[
              <TextAreaInputField
                key="data.task"
                name="data.task"
                label={t('project-task')}
                control={control}
                errors={errors}
                rows={3}
                required
              />,
              <TextAreaInputField
                key="data.purpose"
                name="data.purpose"
                label={t('project-purpose')}
                control={control}
                errors={errors}
                rows={3}
                required
              />,
            ]}
          />
          <ContentItems
            items={[
              <TextAreaInputField
                key="data.overview"
                name="data.overview"
                label={t('project-overview')}
                control={control}
                errors={errors}
                rows={3}
                required
              />,
              <RelatedProjectArrayField
                key="data.relatedProjects"
                operation={operation}
                type="RS"
                ignoredProjectIds={ignoredIdsForRelatedProject}
              />,
            ]}
          />

          <ContentItems
            items={[
              <TextAreaInputField
                key="data.indonesiaData.target"
                name="data.indonesiaData.target"
                label={t('project-target')}
                control={control}
                errors={errors}
                rows={3}
              />,
              <JumlahTargetPenerimaManfaatArrayField key="data.indonesiaData.JumlahTargetPenerimaManfaat" />,
            ]}
          />
        </ContentItem>
      </ContentWrapper>
    </>
  );
};
